import { BounceContainer } from '@components/BounceContainer/BounceContainer'
import { Chain, TransactionType } from '@plug-core/interfaces/common'
import {
  Box,
  CircularProgress,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import React from 'react'
import { format } from 'date-fns'
import useToken from '@plug-core/hooks/useToken'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@utils/routes'
import { FaArrowLeft, FaArrowRight, FaFire, FaKey, FaLeaf } from 'react-icons/fa6'
import { useTranslation } from 'react-i18next'
import { TransactionKind } from '@plug-core/clients/LedgerScannerClient'
import { TransactionActivity, TransactionStatus } from '@plug-core/interfaces/transactionActivity'
import TokenAvatar from '@components/TokenAvatar'
import { formatNumber } from '@plug-core/utils/formatNumber'
import { hasOtherParty } from '@plug-core/hooks/useTransactionOtherParty'
import { e8sToDecimals } from '@plug-core/utils/validationHelper'
import { TransactionOtherParty } from '@modules/Transactions/Components/TransactionOtherParty'
import { getChainIcon } from '@plug-core/utils/getChainIcon'
import { CiCircleAlert } from 'react-icons/ci'

const transactionColors = {
  [TransactionKind.Transfer]: '#4a4a4a',
  [TransactionKind.Mint]: '#2ecc71',
  [TransactionKind.Burn]: '#e74c3c',
  [TransactionKind.Approve]: '#4a4a4a',
}

const getTransactionIcon = (kind: TransactionKind, type: 'send' | 'receive') => {
  switch (kind) {
    case TransactionKind.Mint:
      return <FaLeaf fontSize="small" color="green" />
    case TransactionKind.Burn:
      return <FaFire fontSize="small" color="orange" />
    case TransactionKind.Approve:
      return <FaKey fontSize="small" color="primary" />
    default:
      if (type === 'receive') return <FaArrowLeft fontSize="small" color="secondary" />
      return <FaArrowRight fontSize="small" color="primary" />
  }
}

const getTransactionLabel = (
  transaction: TransactionActivity,
  t: (key: string) => string
): string => {
  if (transaction.status === TransactionStatus.Failed) {
    return t('failed')
  }

  if (
    transaction.kind === TransactionKind.Transfer &&
    transaction.status === TransactionStatus.Pending
  ) {
    return t('pending')
  }

  if (transaction.kind === TransactionKind.Transfer) {
    return t(`transactionKinds.Transfer.${transaction.type}`)
  }

  return t(`transactionKinds.${transaction.kind}`)
}

export interface TransactionsListItemProps {
  transaction: TransactionActivity
}

const TransactionInfo = ({ transaction }: { transaction: TransactionActivity }) => {
  const { t } = useTranslation('', { keyPrefix: 'transactionHistory.details' })

  return (
    <>
      {format(transaction.timestamp, 'dd MMM')}
      {hasOtherParty(transaction) && (
        <>
          {' '}
          • {transaction.type === TransactionType.Send ? t('to') : t('from')}{' '}
          <TransactionOtherParty transaction={transaction} />
        </>
      )}
    </>
  )
}

export const TransactionListItem = ({ transaction }: TransactionsListItemProps) => {
  const { logo, decimals } =
    transaction.decimals && transaction.logo
      ? { logo: transaction.logo, decimals: transaction.decimals }
      : useToken(transaction.tokenLedgerId)

  const navigate = useNavigate()
  const { t } = useTranslation('', { keyPrefix: 'transactionHistory' })

  const renderTransactionAmount = ({ type, amount }: TransactionActivity) => {
    const prefix = type === TransactionType.Send ? '-' : '+'

    return `${prefix}${formatNumber(e8sToDecimals(amount, decimals || 8))}`
  }

  return (
    <BounceContainer>
      <ListItem
        onClick={() => {
          if (transaction.currency) {
            navigate(
              ROUTES.TRANSACTION_DETAILS(
                transaction.transactionId.toString(),
                transaction.currency,
                transaction.tokenLedgerId
              ),
              { state: { transaction } }
            )
          }
        }}
      >
        <ListItemAvatar>
          <Stack position="relative">
            {transaction.status === TransactionStatus.Pending && (
              <Box position="absolute" top={-8} left={-8}>
                <CircularProgress size={56} thickness={2} />
              </Box>
            )}
            {transaction.status === TransactionStatus.Failed && (
              <Box position="absolute" top={-8} left={-8}>
                <CiCircleAlert size={56} color="red" />
              </Box>
            )}

            <TokenAvatar logo={logo} />
            {transaction.chain && (
              <Stack
                position="absolute"
                right={12}
                bottom={-6}
                bgcolor="background.paper"
                padding={0.45}
                borderRadius={4}
              >
                <img src={getChainIcon(transaction?.chain)} height={15} width={15} />
              </Stack>
            )}
          </Stack>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography>{getTransactionLabel(transaction, t)}</Typography>
              <Box
                borderRadius="50%"
                width={15}
                height={15}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {getTransactionIcon(transaction?.kind, transaction?.type)}
              </Box>
            </Stack>
          }
          secondary={<TransactionInfo transaction={transaction} />}
          secondaryTypographyProps={{ fontSize: 13, fontWeight: 400 }}
        />

        <ListItemText
          primaryTypographyProps={{
            textAlign: 'right',
            color: transaction.type === 'receive' ? 'rainbow.green' : undefined,
            fontSize: 13,
          }}
          secondaryTypographyProps={{
            textAlign: 'right',
            fontSize: 13,
          }}
          primary={renderTransactionAmount(transaction)}
          secondary={transaction.currency}
        />
      </ListItem>
    </BounceContainer>
  )
}
